import styled from '@emotion/styled'
import { createPropFilter, WebLink, WebLinkProps } from '@news-mono/web-common'
import { fonts } from '../..'
import { playButtonIconClass } from '../../buttons/PlayButton/PlayButton.styled'
import { StyledLink as CardTextInteractions } from '../../cards/CardText/CardTextInteractions.styled'
import { EditorialLabel } from '../../typography/EditorialLabel/EditorialLabel'
import { StyledLiveBubble } from '../../typography/EditorialLabel/EditorialLabel.styled'
import { colors } from '../../__styling/settings/colors'
import { metrics, ThemeMargins } from '../../__styling/settings/metrics'
import {
    breakpoint,
    breakpointBetween,
    breakpointMax,
} from '../../__styling/style-functions'
import { calcRem } from '../../__styling/style-functions/calc-rem'
import { breakWords } from '../../__styling/style-mixins'
import { EntCardByline } from '../CardByline/CardByline'
import { StyledHeadshotWrapperClassName } from '../CardByline/CardByline.styled'
import { CardMedia, MediaMode } from '../CardMedia/CardMedia'
import { TeaserMode } from '../CardText/CardTeaser'
import { CardText } from '../CardText/CardText'
import { StyledHeadlineText, StyledTeaser } from '../CardText/CardText.styled'
import { CardOrientationOptions, CardType } from './SNEntCard'

export interface StyledSNEntCardProps {
    hasBackground?: boolean
    hasSeparator?: boolean
    hasPadding?: boolean
    mediaMode?: MediaMode
    verticalSpacing?: keyof ThemeMargins
    isLarge?: boolean
    cardOrientation?: CardOrientationOptions
    fullWidthOnMobile?: boolean
}

export const StyledSNEnt = styled('div')<StyledSNEntCardProps>(
    {
        ...breakWords,
        position: 'relative',
        width: '100%',
        display: 'flex',
        flexWrap: 'wrap',
        flexGrow: 1,
        alignItems: 'stretch',
        borderRadius: '1px',
        lineHeight: '19px',
        boxShadow: `0 0px 7px rgba(0,0,0,0.25)`,
    },
    ({
        verticalSpacing,
        theme,
        hasBackground,
        fullWidthOnMobile,
        hasSeparator,
        hasPadding,
    }) => ({
        marginBottom: verticalSpacing && theme.margins[verticalSpacing],
        borderBottom: hasSeparator
            ? `1px solid ${colors.sevennews.greyMarble}`
            : undefined,
        background: hasBackground
            ? theme.colors.background.secondary
            : theme.colors.background.primary,
        padding: hasPadding ? calcRem(theme.cards.outerMarginLarge) : undefined,
        paddingBottom: calcRem(7),
        [breakpointMax('sm')]: {
            marginBottom: verticalSpacing && theme.margins[verticalSpacing],
        },
    }),
)

export interface StyledLinkProps {
    isLarge?: boolean
    hasBackground?: boolean
    cardOrientation?: CardOrientationOptions
    hasPadding?: boolean
    fullWidthOnMobile?: boolean
    isFunded?: boolean
    displayVideoIcon?: boolean
}

export const StyledLink = styled(CardTextInteractions, {
    shouldForwardProp: createPropFilter<WebLinkProps>()([
        'isLarge',
        'hasBackground',
        'cardOrientation',
        'hasPadding',
        'fullWidthOnMobile',
        'isFunded',
    ]),
})<StyledLinkProps>(
    {
        textDecoration: 'none',
        display: 'flex',
        flexFlow: 'column nowrap',
        justifyContent: 'space-between',
        width: '100%',
        textDecorationSkipInk: 'auto',
    },

    ({ fullWidthOnMobile, hasPadding, theme, isLarge, displayVideoIcon }) => [
        fullWidthOnMobile &&
            !hasPadding && {
                [breakpointMax('sm')]: {
                    padding: isLarge
                        ? calcRem(16)
                        : calcRem(theme.cards.outerMargin),
                },
            },

        {
            '&:focus': {
                outline: `solid 2px #C0A9F6`,
            },

            [`& .${playButtonIconClass}`]: {
                opacity: displayVideoIcon ? 1 : 0,
            },
            '&:hover': {
                [`& ${StyledHeadlineText}`]: {
                    color: 'inherit',
                },
            },
        },
        {
            marginBottom: isLarge ? calcRem(48) : calcRem(40),
            [breakpoint('sm')]: {
                marginBottom: calcRem(40),
            },
        },
        isLarge && {
            '& [class*="StyledHeadline"]': {
                fontSize: calcRem(16),
                [breakpoint('sm')]: {
                    fontSize: calcRem(20),
                },
                [breakpoint('md')]: {
                    fontSize: calcRem(25),
                },
            },
        },
    ],
)
StyledLink.displayName = 'StyledLink'

interface StyledCardMediaAndTextProps {
    cardType?: CardType
}

export const StyledCardMediaAndText = styled(
    'div',
)<StyledCardMediaAndTextProps>(({ cardType }) => ({
    ...(cardType && cardType === 'small'
        ? {
              display: 'flex',
              flexDirection: 'row-reverse',

              div: {
                  flex: 1,
              },
              [`${StyledEditorialLabel}`]: {
                  bottom: calcRem(17),
                  fontSize: calcRem(16),
                  padding: calcRem(3, 8, 3, 5),
              },
          }
        : {}),
}))
interface StyledCardMediaProps {
    isLarge?: boolean
    hasBackground?: boolean
    cardOrientation?: CardOrientationOptions
    mediaMode?: MediaMode
    fullWidthOnMobile?: boolean
}

const mobileImageWidth = 110
const tabletImageWidth = 210

export const StyledCardMediaWrapper = styled('div')(() => ({
    position: 'relative',
}))

export const StyledCardMedia = styled(CardMedia)<StyledCardMediaProps>(
    ({ cardOrientation }) => ({
        // Different image widths based on the Breakpoint for the card
        ...(cardOrientation && cardOrientation.type === 'Landscape-SNEnt'
            ? {
                  [breakpointMax('sm')]: {
                      width: '100%',
                      alignSelf: 'flex-start', //ensures image container does not stretch to height of text column breaking interaction states
                      margin: calcRem(0, metrics.sevennews.margins.sm, 0, 0),
                  },
              }
            : {}),

        ...(cardOrientation &&
        cardOrientation.type === 'Landscape-SNEnt' &&
        cardOrientation.maxBreakpoint === 'md'
            ? {
                  [breakpointBetween('sm', 'md')]: {
                      width: tabletImageWidth,
                      marginRight: calcRem(metrics.sevennews.margins.md),
                  },
              }
            : {}),
    }),
    ({ mediaMode }) =>
        mediaMode === 'edgeToEdge' && {
            [breakpoint('sm')]: {
                width: `calc(100% + ${
                    metrics.sevennews.cards.outerMarginLarge * 2
                }px)`,
                margin: calcRem(
                    -metrics.sevennews.cards.outerMarginLarge,
                    -metrics.sevennews.cards.outerMarginLarge,
                    metrics.sevennews.margins.sm,
                    -metrics.sevennews.cards.outerMarginLarge,
                ),
            },
        },

    ({ fullWidthOnMobile }) =>
        fullWidthOnMobile && {
            [breakpointMax('sm')]: {
                width: '100%',
                margin: calcRem(0, 0, metrics.sevennews.margins.sm, 0),
            },
        },
)

export const StyledEditorialLabel = styled(EditorialLabel)({
    position: 'absolute',
    bottom: calcRem(8),
    left: calcRem(8),
    background: colors.sevennews.red,
    textTransform: 'capitalize',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '2px',
    padding: calcRem(5, 16, 5, 10),
    [`${StyledLiveBubble}`]: {
        width: calcRem(7),
        height: calcRem(7),
        marginRight: calcRem(3),
    },
})

interface StyledCardTextProps {
    cardOrientation?: CardOrientationOptions
    fullWidthOnMobile?: boolean
    teaserMode: TeaserMode
    isLarge?: boolean
    cardType?: CardType
}

export const StyledCardText = styled(CardText)<StyledCardTextProps>(
    ({ cardOrientation, fontScale, isLarge, cardType }) => ({
        [`${StyledHeadlineText}`]: {
            fontFamily: fonts.sevennews.headingAlternative,
        },
        ...(cardOrientation && cardOrientation.type === 'Landscape-SNEnt'
            ? {
                  [breakpointMax('xs')]: {
                      [`& .${StyledTeaser}`]: {
                          display: 'none',
                      },
                  },
              }
            : {}),

        ...(cardOrientation &&
        cardOrientation.type === 'Landscape-SNEnt' &&
        cardOrientation.maxBreakpoint === 'md'
            ? {
                  // Stops breakpoints being overwritten
                  [breakpointBetween('sm', 'md')]: {
                      width: `calc(100% - (${
                          tabletImageWidth + metrics.sevennews.margins.md
                      }px))`,

                      [`& ${StyledHeadlineText}`]: {
                          fontSize:
                              fontScale && calcRem(Math.ceil(18 * fontScale)),
                      },
                  },
              }
            : {}),

        ...(isLarge && {
            [breakpoint('md')]: {
                [`& ${StyledHeadlineText}`]: {
                    fontSize: calcRem(30),
                },
            },
        }),
        ...(cardType &&
            cardType === 'small' && {
                [breakpoint('md')]: {
                    [`& ${StyledHeadlineText}`]: {
                        lineHeight: calcRem(16),
                    },
                },
                paddingRight: calcRem(15),
            }),
        ...(cardType &&
            cardType === 'medium' && {
                [`& ${StyledHeadlineText}`]: {
                    fontSize: calcRem(19),
                },
                [breakpoint('sm')]: {
                    [`& ${StyledHeadlineText}`]: {
                        fontSize: calcRem(16),
                    },
                },
                [breakpoint('md')]: {
                    [`& ${StyledHeadlineText}`]: {
                        fontSize: calcRem(19),
                    },
                },
            }),
    }),
)

export interface StyledCardBylineProps {
    cardOrientation?: CardOrientationOptions
    hasPadding?: boolean
    mediaMode?: MediaMode
}

export const StyledCardByline = styled(EntCardByline)<StyledCardBylineProps>(
    ({ cardOrientation }) => ({
        ...(cardOrientation && cardOrientation.type === 'Landscape-SNEnt'
            ? {
                  [breakpointMax('sm')]: {
                      // If byline contains a headshot, hide the headshot on mobile
                      [`& .${StyledHeadshotWrapperClassName}`]: {
                          display: 'none',
                      },
                  },
              }
            : {}),
    }),
    {
        position: 'absolute',
        bottom: calcRem(12),
        width: '100%',
    },
)

export interface StyledCardArrowWrapperProps {
    cardType?: string
    hasPadding?: boolean
    topic?: string
}

export const StyledCardArrowWrapper = styled(WebLink, {
    shouldForwardProp: createPropFilter<StyledCardArrowWrapperProps>()([
        'cardType',
        'hasPadding',
    ]),
})<StyledCardArrowWrapperProps>(({ cardType, hasPadding, topic, theme }) => ({
    position: 'absolute',
    bottom: hasPadding ? 0 : 0,
    right: hasPadding ? 0 : 0,
    width: cardType === 'large' ? calcRem(45) : calcRem(20),
    height: cardType === 'large' ? calcRem(60) : calcRem(35),
    svg: {
        position: 'absolute',
        transform: cardType === 'large' ? 'scale(1.5)' : 'inherit',
        bottom:
            cardType === 'large'
                ? calcRem(20)
                : calcRem(theme.cards.outerMarginLarge),
        left: calcRem(-20),
        path: {
            stroke: theme.sectionValues.primaryColor,
        },
        [breakpoint('sm')]: {
            bottom:
                cardType === 'large'
                    ? calcRem(32)
                    : calcRem(theme.cards.outerMarginLarge),
        },
    },
}))

export const smallCardFoldRemWidth = calcRem(17)

export const smallCardFoldRemHeight = calcRem(34)

export const largeCardFoldRemWidth = calcRem(35)

export const largeCardFoldRemHeight = calcRem(60)

export const StyledCardFold = styled('span')<StyledCardArrowWrapperProps>(
    ({ cardType, topic, theme }) => ({
        position: 'absolute',
        bottom: 0,
        right: 0,
        border: 'none',
        background: theme.sectionValues.primaryColor,
        width:
            cardType === 'large'
                ? largeCardFoldRemWidth
                : cardType === 'medium'
                ? calcRem(25)
                : smallCardFoldRemWidth,
        height:
            cardType === 'large'
                ? largeCardFoldRemHeight
                : cardType === 'medium'
                ? calcRem(45)
                : smallCardFoldRemHeight,
        clipPath: 'polygon(100% 0, 0 100%, 100% 100%)',
    }),
)

export const StyledPlayButtonWrapper = styled('div')({
    width: `calc(100% - ${calcRem(9)})`,
    height: '100%',
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    top: 0,
    '& [class*="StyledPlayButton"]': {
        position: 'unset',
        marginTop: 0,
        marginLeft: 0,
    },
    [breakpoint('sm')]: {
        height: `calc(100% - ${calcRem(9)})`,
        width: '100%',
    },
})
